import { SignupState } from '../../../../store/signup/types';
import { object, string } from 'yup';
import { signSetAction } from '../../../../store/signup/actions';
import Button from '@material-ui/core/Button';
import Space from '../../../../theme/Space';
import { useForm } from 'react-hook-form';
import { zipRegExp, phoneRegExp } from '../../utils/validationRegExp';
import { TextInput } from '../../../../theme/TextInput';
import { addPhoneNumberFormatting } from '../../../../store/signup/reducer';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from 'store/hooks';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { normalizeApostrophes } from 'pages/signup/utils/normalizeApostrophes';

interface EditFormProps {
    handleClose: () => void;
    signUp: SignupState;
}

export const EditForm: React.FC<EditFormProps> = ({ handleClose, signUp }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const copy = useMemo(
        () => ({
            continue: t('labels.continue'),
            organizationName: t(
                'signup.reviewStep.textFields.organizationName',
            ),
            organizationAddress: t(
                'signup.reviewStep.textFields.organizationAddress',
            ),
            organizationZip: t('signup.reviewStep.textFields.organizationZip'),
            organizationPhoneNumber: t(
                'signup.reviewStep.textFields.organizationPhoneNumber',
            ),
            ein: t('signup.reviewStep.textFields.ein'),
            required: t('error.required'),
            zipRequired: t('error.signup.zip'),
            phoneRequired: t('error.signup.phone'),
        }),
        [t],
    );

    const form = useForm<Partial<SignupState>>({
        mode: 'onChange',
        resolver: yupResolver(
            object<Partial<SignupState>>({
                organizationName: string().required('required').max(255),
                organizationAddress: string().required('required').max(255),
                organizationZip: string()
                    .matches(zipRegExp, copy.zipRequired)
                    .max(5),
                organizationPhoneNumber: string()
                    .matches(phoneRegExp, copy.phoneRequired)
                    .max(25),
            }),
        ),
    });

    const handleSubmit = async () => {
        const values = form.getValues();
        dispatch(
            signSetAction({
                organizationName: normalizeApostrophes(values.organizationName),
                organizationAddress: normalizeApostrophes(values.organizationAddress),
                organizationZip: values.organizationZip,
                organizationPhoneNumber: values.organizationPhoneNumber,
                ein: values.ein,
            }),
        );
        handleClose();
    };

    useEffect(() => {
        form.trigger();
        //eslint-disable-next-line
    }, []);

    return (
        <form onSubmit={form.handleSubmit(handleSubmit)}>
            <TextInput<Partial<SignupState>>
                type="text"
                name="organizationName"
                label={copy.organizationName}
                placeholder={copy.organizationName}
                formRef={form}
                defaultValue={signUp.organizationName}
            />
            <TextInput<Partial<SignupState>>
                type="text"
                name="organizationAddress"
                label={copy.organizationAddress}
                placeholder={copy.organizationAddress}
                formRef={form}
                defaultValue={signUp.organizationAddress}
            />
            {signUp.organizationCountry !== 'bs' && (
                <TextInput<Partial<SignupState>>
                    type="text"
                    name="organizationZip"
                    label={copy.organizationZip}
                    placeholder={copy.organizationZip}
                    formRef={form}
                    defaultValue={signUp.organizationZip}
                />
            )}
            <TextInput<Partial<SignupState>>
                type="text"
                name="organizationPhoneNumber"
                label={copy.organizationPhoneNumber}
                placeholder={copy.organizationPhoneNumber}
                formRef={form}
                defaultValue={addPhoneNumberFormatting(
                    signUp.organizationPhoneNumber,
                )}
            />
            <Space gap={3} />
            <Button
                type="submit"
                variant="contained"
                disabled={!form.formState.isValid}
            >
                {copy.continue}
            </Button>
        </form>
    );
};
